/**
 * useCytoscapeContextMenu Hook
 *
 * Manages Cytoscape context menu for node and group actions
 * Includes tooltip labels on hover
 */

import type { Core, EdgeSingular, NodeSingular } from 'cytoscape'
import { useEffect, useRef } from 'react'

export interface ContextMenuOptions {
  onAddNote?: (nodeId: string) => void
  onAddFinding?: (nodeId: string) => void
  onAddConnection?: (nodeId: string) => void
  onExpandNode?: (nodeId: string) => Promise<void>
  onManageConnection?: (edgeId: string, sourceId: string, targetId: string) => void
  onAddGroupNote?: (groupId: string) => void
  onCreateGroup?: (nodeIds: string[]) => void
  onAddToSandbox?: (nodeId: string) => void
  onFindPathFromHere?: (nodeId: string) => void
  onToggleSuspect?: (nodeId: string) => void
  onToggleCrimeRelated?: (nodeId: string) => void
  /** Look the node up in the CZ/SK register snapshots. Only offered on the
   *  entity types the registers actually index — see REGISTER_LOOKUP_TYPES. */
  onLookUpInRegisters?: (nodeId: string) => void
}

/** Entity types a register lookup is offered on. Companies and people are
 *  what the CZ/SK snapshots index; an Organization is included because the
 *  analyst may well have typed a registered body in as one. */
const REGISTER_LOOKUP_TYPES: readonly string[] = ['Person', 'Company', 'Organization']

// Tooltip element ID
const TOOLTIP_ID = 'cxtmenu-tooltip'

interface ContextMenuInstance {
  destroy: () => void
}

interface CoreWithContextMenu extends Core {
  cxtmenu: (options: Record<string, unknown>) => ContextMenuInstance
}

/**
 * Creates or gets the tooltip element
 */
function getOrCreateTooltip(): HTMLDivElement {
  let tooltip = document.getElementById(TOOLTIP_ID) as HTMLDivElement
  if (!tooltip) {
    tooltip = document.createElement('div')
    tooltip.id = TOOLTIP_ID
    tooltip.style.cssText = `
      position: fixed;
      z-index: 10001;
      background: rgba(24, 24, 27, 0.95);
      color: #e4e4e7;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 500;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.15s ease;
      white-space: nowrap;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      border: 1px solid rgba(113, 113, 122, 0.3);
    `
    document.body.appendChild(tooltip)
  }
  return tooltip
}

/**
 * Shows the tooltip at mouse position
 */
function showTooltip(text: string, x: number, y: number) {
  const tooltip = getOrCreateTooltip()
  tooltip.textContent = text
  tooltip.style.left = `${x + 12}px`
  tooltip.style.top = `${y - 10}px`
  tooltip.style.opacity = '1'
}

/**
 * Hides the tooltip
 */
function hideTooltip() {
  const tooltip = document.getElementById(TOOLTIP_ID) as HTMLDivElement
  if (tooltip) {
    tooltip.style.opacity = '0'
  }
}

export function useCytoscapeContextMenu(cy: Core | null, options: ContextMenuOptions) {
  const optionsRef = useRef(options)
  optionsRef.current = options

  useEffect(() => {
    if (!cy) return

    // Check if cxtmenu extension is available
    const contextMenuCy = cy as CoreWithContextMenu
    if (typeof contextMenuCy.cxtmenu !== 'function') {
      console.warn('cxtmenu extension not available')
      return
    }

    // SVG icons (lucide-react style)
    const icons = {
      sandbox:
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 3h15M6 3v16a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V3M10 7v8m4-8v8"/></svg>',
      route:
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="19" r="3"/><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"/><circle cx="18" cy="5" r="3"/></svg>',
      link: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>',
      stickyNote:
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8Z"/><path d="M15 3v4a2 2 0 0 0 2 2h4"/></svg>',
      expand:
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></svg>',
      layers:
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z"/><path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65"/><path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65"/></svg>',
      settings:
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>',
      alertTriangle:
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>',
      lightbulb:
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6"/><path d="M10 22h4"/></svg>',
      landmark:
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" x2="21" y1="22" y2="22"/><line x1="6" x2="6" y1="18" y2="11"/><line x1="10" x2="10" y1="18" y2="11"/><line x1="14" x2="14" y1="18" y2="11"/><line x1="18" x2="18" y1="18" y2="11"/><polygon points="12 2 20 7 4 7"/></svg>',
      shieldAlert:
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>',
    }

    // Label mappings for tooltips
    const labels: Record<string, string> = {
      sandbox: 'Add to Sandbox',
      route: 'Find Path',
      link: 'Create Connection',
      stickyNote: 'Add Note',
      expand: 'Expand neighbors',
      layers: 'Create Group',
      settings: 'Manage Connection',
      alertTriangle: 'Toggle Suspect',
      lightbulb: 'Add Finding',
      shieldAlert: 'Mark Crime Related',
      landmark: 'Look up in registers',
    }

    // Track current mouse position for tooltip
    let mouseX = 0
    let mouseY = 0
    const trackMouse = (e: MouseEvent) => {
      mouseX = e.clientX
      mouseY = e.clientY
    }
    document.addEventListener('mousemove', trackMouse)

    // Helper to create menu item with hover tooltip
    const createMenuItem = (
      iconKey: keyof typeof icons,
      callback?: (nodeId: string) => void | Promise<void>,
      customLabel?: string
    ) => ({
      fillColor: 'rgba(82, 82, 91, 0.95)',
      content: icons[iconKey],
      contentStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      select: (ele: NodeSingular) => callback?.(ele.id()),
      hover: () => {
        showTooltip(customLabel || labels[iconKey] || iconKey, mouseX, mouseY)
      },
      enabled: !!callback,
    })

    // Common menu options
    const menuOptions = {
      fillColor: 'rgba(63, 63, 70, 0.95)',
      activeFillColor: 'rgba(113, 113, 122, 0.95)',
      activePadding: 6,
      indicatorSize: 24,
      separatorWidth: 1,
      spotlightPadding: 2,
      adaptativeNodeSpotlightRadius: true,
      minSpotlightRadius: 18,
      maxSpotlightRadius: 36,
      openMenuEvents: 'cxttapstart taphold',
      itemColor: '#e4e4e7',
      itemTextShadowColor: 'transparent',
      zIndex: 9999,
      atMouse: false,
    }

    // Build the ring for ONE node. cxtmenu re-evaluates this on every open
    // (`options.commands` may be a function of the target), which is what lets
    // an entry be offered on some entity types and not others — the alternative,
    // a second cxtmenu instance per type, needs every other instance's selector
    // narrowed in lockstep and silently double-renders when one drifts.
    const buildCommands = (typeLabel: string | undefined) => {
      if (typeLabel === 'SourceSnippet') return []
      const commands = []
      const {
        onAddNote,
        onAddFinding,
        onAddConnection,
        onExpandNode,
        onCreateGroup,
        onAddToSandbox,
        onFindPathFromHere,
        onToggleSuspect,
        onToggleCrimeRelated,
        onLookUpInRegisters,
      } = optionsRef.current

      // Add to Sandbox
      if (onAddToSandbox) {
        commands.push(createMenuItem('sandbox', onAddToSandbox))
      }

      // Find Path
      if (onFindPathFromHere) {
        commands.push(createMenuItem('route', onFindPathFromHere))
      }

      // Create Connection
      if (onAddConnection) {
        commands.push(createMenuItem('link', onAddConnection))
      }

      // Add Note
      if (onAddNote) {
        commands.push(createMenuItem('stickyNote', onAddNote, 'Add Note'))
      }

      // Add Finding
      if (onAddFinding) {
        commands.push(createMenuItem('lightbulb', onAddFinding, 'Add Finding'))
      }

      // Expand neighbors
      if (onExpandNode) {
        commands.push(createMenuItem('expand', onExpandNode))
      }

      // Create Group
      if (onCreateGroup) {
        commands.push(
          createMenuItem('layers', (nodeId: string) => onCreateGroup([nodeId]), 'Create Group')
        )
      }

      // Look up in registers (only the entity types the registers index)
      if (onLookUpInRegisters && REGISTER_LOOKUP_TYPES.includes(typeLabel ?? '')) {
        commands.push(createMenuItem('landmark', onLookUpInRegisters))
      }

      // Toggle Suspect (only for Person nodes)
      if (typeLabel === 'Person' && onToggleSuspect) {
        commands.push(createMenuItem('alertTriangle', onToggleSuspect))
      }

      // Toggle Crime Related (for all nodes)
      if (onToggleCrimeRelated) {
        commands.push(createMenuItem('shieldAlert', onToggleCrimeRelated))
      }

      return commands
    }

    // One menu for every node; which entries it offers is decided per node from
    // its typeLabel.
    const nodeMenuInstance = contextMenuCy.cxtmenu({
      selector: 'node',
      commands: (ele: NodeSingular) => buildCommands(ele.data('typeLabel')),
      ...menuOptions,
    })

    // Initialize edge context menu (only for POSSIBLE_MATCH relationships)
    const edgeMenuInstance = contextMenuCy.cxtmenu({
      selector: 'edge[type = "POSSIBLE_MATCH"]',
      commands: () => {
        const onManageConnection = optionsRef.current.onManageConnection
        return onManageConnection
          ? [
              {
                fillColor: 'rgba(82, 82, 91, 0.95)',
                content: icons.settings,
                contentStyle: {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                },
                select: (ele: EdgeSingular) =>
                  onManageConnection(ele.id(), ele.source().id(), ele.target().id()),
                hover: () => {
                  showTooltip('Manage Connection', mouseX, mouseY)
                },
                enabled: true,
              },
            ]
          : []
      },
      ...menuOptions,
    })

    // Hide tooltip when menu closes. Named so the cleanup below can remove it.
    const onMenuClose = () => hideTooltip()
    cy.on('cxttapend tapend', onMenuClose)

    // Cleanup on unmount
    return () => {
      // Remove mouse listener
      document.removeEventListener('mousemove', trackMouse)

      // Hide and remove tooltip
      cy.off('cxttapend tapend', onMenuClose)
      hideTooltip()

      nodeMenuInstance.destroy()
      edgeMenuInstance.destroy()
    }
    // Keep the plugin instance alive while callback props change. cxtmenu
    // disables panning and zooming for an open gesture, but its destroy()
    // does not restore them. Rebuilding here during a fresh graph's async
    // rerenders could therefore leave the canvas permanently locked.
  }, [cy])
}
